import {
  getFlights,
  getNotices,
  getPlan,
  NotFound,
  setNotices,
  subscribePush,
  testPush,
  type FlightEvent,
  type NoticeEvent,
  type NoticePref,
  type Notices,
  type Plan,
  type WatchedFlight,
} from '@trippath/shared';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';

import { useLive } from '../../app/live';
import { wallOf } from '../../lib/clock';

type State =
  | { kind: 'loading' }
  | { kind: 'missing' }
  | { kind: 'failed' }
  | { kind: 'ready'; plan: Plan; flights: WatchedFlight[] };

/** The services by their own names. */
const SERVICES: Record<string, string> = {
  aerodatabox: 'AeroDataBox',
  airlabs: 'AirLabs',
  aviationstack: 'Aviationstack',
};

/** Kinds of event a calendar alarm is made for; the others ring nowhere but the phone. */
const ALARMED = new Set<NoticeEvent>(['checkin', 'tomorrow', 'border']);

/** The kinds told of here; the list's own are on the Lists page. */
const FLIGHT_EVENTS = new Set<NoticeEvent>([
  'delay',
  'gate',
  'checkin',
  'tomorrow',
  'border',
  'free',
  'pay',
  'companion',
]);

/** The events that are told loud on the card: a delay, a move, a cancellation. */
const HOT = new Set<FlightEvent['kind']>(['delay', 'gate', 'terminal', 'cancelled']);

/** A browser's base64url key as the bytes `pushManager.subscribe` takes. */
export function keyBytes(key: string): Uint8Array<ArrayBuffer> {
  const plain = atob(
    key
      .replace(/-/g, '+')
      .replace(/_/g, '/')
      .padEnd(Math.ceil(key.length / 4) * 4, '='),
  );
  return Uint8Array.from(plain, (c) => c.charCodeAt(0));
}

/** Which of a trip's flights the card shows: the first not landed long ago, else the last. */
export function current(flights: WatchedFlight[], now: number): WatchedFlight | null {
  return (
    flights.find(
      (flight) =>
        Date.parse(flight.actualArrives ?? flight.arrives ?? flight.departs) + 3_600_000 > now,
    ) ??
    flights.at(-1) ??
    null
  );
}

/**
 * A flight watched and how one is told (the `FlightWatch` mockup): the flight on its day — when
 * it leaves and lands as its service says now beside what was planned, its terminal, gate,
 * check-in desks and belt, and each change found — the account's choice of how it is told of
 * each kind of event, this device told by Web Push, when the flight is asked, and why a calendar
 * alarm too.
 */
export function FlightWatch() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const locale = lang === 'et' ? 'et-EE' : 'en-GB';
  const { id = '' } = useParams();
  const { version: changed } = useLive();
  const [state, setState] = useState<State>({ kind: 'loading' });
  const [picked, setPicked] = useState<string | null>(null);
  const [notices, setNoticesState] = useState<Notices | null>(null);
  const [said, setSaid] = useState<string | null>(null);
  const [now] = useState(() => Date.now());

  useEffect(() => {
    let live = true;
    Promise.all([getPlan(id), getFlights(id)]).then(
      ([plan, flights]) => {
        if (live) setState({ kind: 'ready', plan, flights });
      },
      (error: unknown) => {
        if (live) setState({ kind: error instanceof NotFound ? 'missing' : 'failed' });
      },
    );
    return () => {
      live = false;
    };
  }, [id, changed]);

  useEffect(() => {
    getNotices().then(setNoticesState, () => undefined);
  }, []);

  if (state.kind === 'loading') return <p className="page quiet">{t('trips.loading')}</p>;
  if (state.kind !== 'ready') {
    return (
      <div className="page">
        <p className="form-failed" role="alert">
          {t(state.kind === 'missing' ? 'trips.notFound' : 'trips.failed')}
        </p>
        <Link to="/">{t('trips.back')}</Link>
      </div>
    );
  }
  const { plan, flights } = state;
  const flight =
    flights.find((one) => `${one.booking} ${String(one.leg)}` === picked) ??
    current(flights, now);
  // A stop's city by the booking its ticket is, and the next one's.
  // A stop's city by the booking its ticket is, and the next one's; else the airports' codes.
  const cities = (watched: WatchedFlight) => {
    const at = plan.stops.findIndex((stop) =>
      stop.bookings.some((b) => b.id === watched.booking),
    );
    const [here, next] = [plan.stops[at], plan.stops[at + 1]];
    return here
      ? [here.city, next?.city ?? watched.destination]
      : [watched.origin, watched.destination];
  };
  const clock = (at: string | null, zone: string | null) =>
    at === null ? '' : wallOf(at, zone).time;
  const dayOf = (at: string, zone: string | null) =>
    new Intl.DateTimeFormat(locale, {
      weekday: 'short',
      day: 'numeric',
      month: 'long',
      timeZone: zone ?? 'UTC',
    }).format(new Date(at));

  const statusOf = (watched: WatchedFlight) => {
    if (watched.status === 'cancelled') return { text: t('flights.cancelled'), tone: 'alert' };
    if (watched.delay !== null && watched.delay >= 15) {
      return { text: t('flights.late', { minutes: watched.delay }), tone: 'warn' };
    }
    if (watched.status === 'landed') return { text: t('flights.landed'), tone: 'ok' };
    if (watched.status === 'departed') return { text: t('flights.departed'), tone: 'ok' };
    if (watched.checkedAt === null) return { text: t('flights.notYet'), tone: 'plain' };
    return { text: t('flights.onTime'), tone: 'ok' };
  };

  const eventWords = (watched: WatchedFlight, event: FlightEvent): [string, string] => {
    const detail = event.detail as {
      minutes?: number;
      departs?: string;
      from?: string | null;
      to?: string;
      gate?: string | null;
    };
    const service = SERVICES[event.service] ?? event.service;
    const by = service === '' ? '' : ` · ${service}`;
    const moved = `${detail.from ?? '—'} → ${detail.to ?? ''}`;
    switch (event.kind) {
      case 'delay':
        return [
          t('flights.late', { minutes: detail.minutes ?? 0 }),
          `${t('flights.leaves', { time: clock(detail.departs ?? null, watched.originZone) })}${by}`,
        ];
      case 'ontime':
        return [
          t('flights.onTime'),
          detail.gate ? t('flights.atGate', { gate: detail.gate }) : '',
        ];
      case 'gate':
        return [t('flights.gateMoved'), `${moved}${by}`];
      case 'terminal':
        return [t('flights.terminalMoved'), `${moved}${by}`];
      case 'checkin':
        return [t('flights.checkInDesks'), `${detail.to ?? ''}${by}`];
      case 'belt':
        return [t('flights.beltSaid'), `${detail.to ?? ''}${by}`];
      default:
        return [t(`flights.${event.kind}`), service];
    }
  };

  const keep = (had: Notices, next: NoticePref[]) => {
    setNoticesState({ ...had, prefs: next });
    setNotices(next).then(
      () => undefined,
      () => {
        setSaid(t('flights.notKept'));
      },
    );
  };
  const toggle = (had: Notices, event: NoticeEvent, how: 'push' | 'email' | 'calendar') => {
    keep(
      had,
      had.prefs.map((pref) => (pref.event === event ? { ...pref, [how]: !pref[how] } : pref)),
    );
  };
  const turnOn = async () => {
    setSaid(null);
    const key = notices?.key;
    if (
      key === null ||
      key === undefined ||
      !('Notification' in window) ||
      !('serviceWorker' in navigator)
    ) {
      setSaid(t('flights.noPush'));
      return;
    }
    if ((await Notification.requestPermission()) !== 'granted') {
      setSaid(t('flights.denied'));
      return;
    }
    const worker = await navigator.serviceWorker.ready;
    const made = await worker.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: keyBytes(key),
    });
    const { endpoint = '', keys = {} } = made.toJSON();
    await subscribePush({
      endpoint,
      keys: { p256dh: keys.p256dh ?? '', auth: keys.auth ?? '' },
    });
    setNoticesState(await getNotices());
    setSaid(t('flights.turnedOn'));
  };
  const test = () => {
    testPush().then(
      (sent) => {
        setSaid(t('flights.tested', { count: sent }));
      },
      () => {
        setSaid(t('flights.notKept'));
      },
    );
  };

  const latest = flight?.events.find((event) => HOT.has(event.kind)) ?? null;
  const [from, to] = flight ? cities(flight) : ['', ''];

  return (
    <div className="page flights-page">
      <header className="page-head">
        <h1>{t('flights.title')}</h1>
        <p className="lead">{t('flights.lead')}</p>
      </header>
      <div className="flights-layout">
        <div className="flights-main">
          {flight === null ? (
            <section className="panel" aria-labelledby="flights-none">
              <h2 id="flights-none" className="panel-title">
                {t('flights.title')}
              </h2>
              <p className="quiet">{t('flights.none')}</p>
              <Link to={`/trips/${id}/bookings`}>{t('flights.toBookings')}</Link>
            </section>
          ) : (
            <section className="panel flights-card" aria-labelledby="flights-card">
              <div className="flights-head">
                <h2 id="flights-card" className="panel-title">
                  {flight.number} · {from} → {to} · {dayOf(flight.departs, flight.originZone)}
                </h2>
                <span className={`tag flights-tag-${statusOf(flight).tone}`}>
                  {statusOf(flight).text}
                </span>
              </div>
              {flights.length > 1 && (
                <div className="flights-pick" role="group" aria-label={t('flights.pick')}>
                  {flights.map((one) => (
                    <button
                      key={`${one.booking} ${String(one.leg)}`}
                      type="button"
                      className="button button-small"
                      aria-pressed={one === flight}
                      onClick={() => {
                        setPicked(`${one.booking} ${String(one.leg)}`);
                      }}
                    >
                      {one.number}
                    </button>
                  ))}
                </div>
              )}
              <div className="flights-way">
                <div>
                  <span className="quiet">
                    {flight.origin} · {from}
                  </span>
                  <b>{clock(flight.actualDeparts ?? flight.departs, flight.originZone)}</b>
                  {flight.actualDeparts !== null && (
                    <s>{clock(flight.departs, flight.originZone)}</s>
                  )}
                </div>
                <div className="flights-mid">
                  {flight.arrives !== null &&
                    t('flights.takes', {
                      hours: Math.floor(
                        (Date.parse(flight.arrives) - Date.parse(flight.departs)) / 3_600_000,
                      ),
                      minutes: Math.round(
                        ((Date.parse(flight.arrives) - Date.parse(flight.departs)) %
                          3_600_000) /
                          60_000,
                      ),
                    })}
                  {flight.vehicle !== null && (
                    <>
                      <br />
                      {flight.vehicle}
                    </>
                  )}
                </div>
                <div className="flights-end">
                  <span className="quiet">
                    {flight.destination} · {to}
                  </span>
                  <b>{clock(flight.actualArrives ?? flight.arrives, flight.destinationZone)}</b>
                  {flight.actualArrives !== null && (
                    <s>{clock(flight.arrives, flight.destinationZone)}</s>
                  )}
                </div>
              </div>
              <dl className="flights-facts">
                <div>
                  <dt>{t('flights.terminal')}</dt>
                  <dd>{flight.terminal ?? '—'}</dd>
                </div>
                <div
                  className={
                    flight.events.some((event) => event.kind === 'gate')
                      ? 'flights-changed'
                      : undefined
                  }
                >
                  <dt>
                    {flight.events.some((event) => event.kind === 'gate')
                      ? t('flights.gateChanged')
                      : t('flights.gate')}
                  </dt>
                  <dd>{flight.gate ?? '—'}</dd>
                </div>
                <div>
                  <dt>{t('flights.checkIn')}</dt>
                  <dd>{flight.checkIn ?? '—'}</dd>
                </div>
                <div>
                  <dt>{t('flights.belt', { city: to })}</dt>
                  <dd>{flight.belt ?? '—'}</dd>
                </div>
              </dl>
              <ol className="flights-events" aria-label={t('flights.changes')}>
                {flight.events.map((event) => {
                  const [title, line] = eventWords(flight, event);
                  const at = wallOf(event.at, flight.originZone);
                  const day = wallOf(flight.departs, flight.originZone).day;
                  return (
                    <li
                      key={`${event.at} ${event.kind}`}
                      className={HOT.has(event.kind) ? 'flights-hot' : undefined}
                    >
                      <time dateTime={event.at}>
                        {at.day === day
                          ? at.time
                          : `${at.day.slice(8, 10)}.${at.day.slice(5, 7)} ${at.time}`}
                      </time>
                      <span className="flights-dot" aria-hidden="true" />
                      <span>
                        <b>{title}</b> {line}
                      </span>
                    </li>
                  );
                })}
                {flight.events.length === 0 && (
                  <li>
                    <span />
                    <span className="flights-dot" aria-hidden="true" />
                    <span className="quiet">
                      {t('flights.nextCheck', {
                        time: `${wallOf(flight.nextCheck, flight.originZone).day} ${clock(flight.nextCheck, flight.originZone)}`,
                      })}
                    </span>
                  </li>
                )}
              </ol>
            </section>
          )}
          <section className="panel" aria-labelledby="flights-notices">
            <div className="flights-head">
              <h2 id="flights-notices" className="panel-title">
                {t('flights.notices')}
              </h2>
              <span className="quiet flights-small">{t('flights.yours')}</span>
            </div>
            {notices === null ? (
              <p className="quiet">{t('trips.loading')}</p>
            ) : (
              <>
                <div className="table-scroll">
                  <table className="flights-matrix">
                    <thead>
                      <tr>
                        <th scope="col">{t('flights.event')}</th>
                        <th scope="col">{t('flights.phone')}</th>
                        <th scope="col">{t('flights.email')}</th>
                        <th scope="col">{t('flights.calendar')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {notices.prefs
                        .filter((pref) => FLIGHT_EVENTS.has(pref.event))
                        .map((pref) => (
                          <tr key={pref.event}>
                            <th scope="row">{t(`flights.events.${pref.event}`)}</th>
                            {(['push', 'email', 'calendar'] as const).map((how) => {
                              const can =
                                how === 'calendar'
                                  ? ALARMED.has(pref.event)
                                  : how === 'email'
                                    ? notices.mail
                                    : true;
                              const label = `${t(`flights.${how === 'push' ? 'phone' : how}`)}: ${t(`flights.events.${pref.event}`)}`;
                              return (
                                <td key={how}>
                                  {can ? (
                                    <label className="flights-check">
                                      <input
                                        type="checkbox"
                                        aria-label={label}
                                        checked={pref[how]}
                                        onChange={() => {
                                          toggle(notices, pref.event, how);
                                        }}
                                      />
                                      {pref[how] && how === 'calendar' && (
                                        <span>{t('flights.alarm')}</span>
                                      )}
                                      {pref[how] &&
                                        how === 'push' &&
                                        pref.event === 'tomorrow' && <span>20:00</span>}
                                      {pref[how] && how === 'push' && pref.event === 'pay' && (
                                        <span>08:00</span>
                                      )}
                                    </label>
                                  ) : (
                                    <span
                                      className="quiet"
                                      title={how === 'email' ? t('flights.noMail') : undefined}
                                    >
                                      —
                                    </span>
                                  )}
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
                {!notices.mail && <p className="quiet flights-small">{t('flights.noMail')}</p>}
                <div className="flights-device">
                  <span>{t('flights.devices', { count: notices.devices })}</span>
                  <button
                    type="button"
                    className="button button-small"
                    onClick={() =>
                      void turnOn().catch(() => {
                        setSaid(t('flights.noPush'));
                      })
                    }
                  >
                    {t('flights.turnOn')}
                  </button>
                  {notices.devices > 0 && (
                    <button type="button" className="button button-small" onClick={test}>
                      {t('flights.test')}
                    </button>
                  )}
                </div>
                {said !== null && (
                  <p className="flights-small" role="status">
                    {said}
                  </p>
                )}
              </>
            )}
          </section>
        </div>
        <div className="flights-side">
          {flight && (
            <div className="flights-notif" role="img" aria-label={t('flights.preview')}>
              <span className="flights-app">T</span>
              <span>
                <b>
                  {latest
                    ? `${flight.number} ${eventWords(flight, latest)[0].toLowerCase()}${latest.kind === 'gate' ? `: ${flight.gate ?? ''}` : ''}`
                    : `${flight.number} · ${from} → ${to}`}
                </b>
                <br />
                {t('flights.leaves', {
                  time: clock(flight.actualDeparts ?? flight.departs, flight.originZone),
                })}
                {flight.terminal !== null && ` · ${flight.origin} ${flight.terminal}`}
                <br />
                <small>{t('flights.now')}</small>
              </span>
            </div>
          )}
          <section className="panel" aria-labelledby="flights-schedule">
            <h2 id="flights-schedule" className="panel-title">
              {t('flights.schedule')}
            </h2>
            <dl className="flights-schedule">
              {(['day', 'hours', 'near', 'air'] as const).map((when) => (
                <div key={when}>
                  <dt className="mono">{t(`flights.when.${when}`)}</dt>
                  <dd>{t(`flights.how.${when}`)}</dd>
                </div>
              ))}
            </dl>
            <p className="quiet flights-small">{t('flights.quota')}</p>
          </section>
          <section className="panel" aria-labelledby="flights-offline">
            <h2 id="flights-offline" className="panel-title">
              {t('flights.offline')}
            </h2>
            <p className="flights-small">{t('flights.offline1')}</p>
            <p className="flights-small">{t('flights.offline2')}</p>
          </section>
        </div>
      </div>
    </div>
  );
}
